import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventIcon from "@mui/icons-material/Event";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { memo, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import ExamPacsAction from "../../components/Exams/ExamPacsAction/ExamPacsAction";
import ExamReportAction from "../../components/Exams/ExamReportAction/ExamReportAction";
import ExamsRedirectModal from "../../components/Exams/ExamsRedirectFile/ExamsRedirectModal";
import ModalComponent from "../../components/UI/Modal";
import Row from "../../components/UI/Row";
import { IExams } from "../../models/exams";
import { IExamsWithPacsValidation } from "../../components/Exams/ExamPacsAction/models";

interface IExamItemProps {
  data: IExams | IExamsWithPacsValidation;
  individualId?: string;
  checkPdfSerialOnNullFormat: boolean;
  cdnUrl: boolean;
  hasPixeonExtension: boolean;
  hasVueMotionExtension: boolean;
  hasExamPacsAction: boolean;
  loadingPacsValues: boolean;
  pacsColumnName: string | null;
  renderActions?: (exam: IExams | IExamsWithPacsValidation) => ReactNode;
  showImagesColumn?: boolean;
  imagesColumnTitle?: string | null;
}

const fixMalformedYear = (dateStr: string): string => {
  if (!dateStr) return dateStr;
  const match = dateStr.match(/^(\d{4})(-.+)$/);
  if (!match) return dateStr;
  const year = parseInt(match[1], 10);
  if (year < 100) {
    const correctedYear = 2000 + (year % 100);
    return `${correctedYear}${match[2]}`;
  }
  return dateStr;
};

const translationPath = "page.exams.";

const ExamItem: React.FC<IExamItemProps> = ({
  data,
  individualId,
  checkPdfSerialOnNullFormat,
  cdnUrl,
  hasPixeonExtension,
  hasVueMotionExtension,
  hasExamPacsAction,
  loadingPacsValues,
  pacsColumnName,
  renderActions,
  showImagesColumn = true,
  imagesColumnTitle,
}) => {
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();

  return (
    <>
      <ModalComponent onClose={() => setOpen(false)} open={open}>
        <Stack gap={1}>
          <Row
            label={t(translationPath + "procedure")}
            value={data.medicalCertificateName || "-"}
          />

          <Row
            label={t(translationPath + "doctor")}
            value={data.physicianName || "-"}
          />

          <Row
            label={t(translationPath + "unit")}
            value={data.establishmentName || "-"}
          />

          <Row
            label={t(translationPath + "date")}
            value={dayjs(data.medicalCertificateDate).format("L")}
          />

          {renderActions && (
            <Row
              label={t(translationPath + "actions")}
              value={renderActions(data)}
            />
          )}

          <Row
            label={t(translationPath + "report")}
            value={
              <ExamReportAction
                exam={data}
                cdnUrl={cdnUrl}
                checkPdfSerialOnNullFormat={checkPdfSerialOnNullFormat}
                individualId={individualId}
              />
            }
          />

          {showImagesColumn && data.sequencialImageControls.length > 0 && (
            <ExamsRedirectModal
              title={imagesColumnTitle}
              sequencialControl={data.sequencialControl}
              sequencialImageControls={data.sequencialImageControls}
            />
          )}

          {hasExamPacsAction && (
            <>
              {loadingPacsValues && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                  }}
                >
                  <CircularProgress size={15} />
                </Box>
              )}
              {!loadingPacsValues && (
                <Row
                  label={pacsColumnName || t(translationPath + "pacs")}
                  value={
                    <ExamPacsAction
                      exam={data}
                      hasVueMotionExtension={hasVueMotionExtension}
                      hasPixeonExtension={hasPixeonExtension}
                    />
                  }
                />
              )}
            </>
          )}
        </Stack>
      </ModalComponent>
      <Stack
        sx={(t) => ({
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 1,
          backgroundColor: t.palette.divider,
          borderRadius: t.shape.borderRadius / 3,
        })}
        onClick={() => setOpen(true)}
      >
        <Stack>
          <Typography sx={{ fontWeight: "bold" }}>
            {data.medicalCertificateName}
          </Typography>
          <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
            <EventIcon />
            <Typography>
              {dayjs(fixMalformedYear(data.medicalCertificateDate)).format("L")}
            </Typography>
          </Stack>
        </Stack>
        <ChevronRightIcon />
      </Stack>
    </>
  );
};

export default memo(ExamItem);
